// 2026-10-05 pulido auditado: el disable file-wide de
// react-hooks/set-state-in-effect se acota a los 2 effects de sync (bloques
// disable/enable por effect). No se migra a useSyncExternalStore: el estado
// es local con setters expuestos (los tests y ChatWorkspace los escriben
// directo) — un store externo cambiaría el contrato del hook por 0 avisos
// (la regla YA está en off para los 9 splits de eslint.config.mjs:79-95;
// aquí el sync logout + derived→quotaExceeded es intencional y queda
// marcado in situ).
import { useEffect, useMemo, useState } from "react";
import type { PlanTier } from "@/types";

export interface UseChatQuotasReturn {
  imageQuotaRemaining: number | null;
  setImageQuotaRemaining: React.Dispatch<React.SetStateAction<number | null>>;
  imageQuotaResetIn: number | null;
  setImageQuotaResetIn: React.Dispatch<React.SetStateAction<number | null>>;
  chatQuotaRemaining: number | null;
  setChatQuotaRemaining: React.Dispatch<React.SetStateAction<number | null>>;
  /** Current plan tier from /api/usage (drives the quota_hit upsell ladder) — null until the first fetch. */
  plan: PlanTier | null;
  setPlan: React.Dispatch<React.SetStateAction<PlanTier | null>>;
  /** Smart quota remaining (cuota separada 2026-09-18) — null hasta el primer fetch. */
  smartQuotaRemaining: number | null;
  setSmartQuotaRemaining: React.Dispatch<React.SetStateAction<number | null>>;
  /** Smart trial lifetime restante (2026-09-26, SSOT smartTrialTotal): solo
   * free lo expone /api/usage (derivado total − usado); null = desconocido
   * (fail-open visual: sin contador ni nudge hasta el primer fetch). */
  smartTrialRemaining: number | null;
  setSmartTrialRemaining: React.Dispatch<React.SetStateAction<number | null>>;
  /** Tokens remaining (authoritative for "Tokens used today" display) — separate from min() blocked state */
  tokensRemaining: number | null;
  setTokensRemaining: React.Dispatch<React.SetStateAction<number | null>>;
  quotaExceeded: boolean;
  setQuotaExceeded: React.Dispatch<React.SetStateAction<boolean>>;
  quotaResetInSeconds: number | null;
  setQuotaResetInSeconds: React.Dispatch<React.SetStateAction<number | null>>;
  refresh: (force?: boolean) => Promise<void>;
}

/**
 * Polls /api/usage every 180s and syncs daily reset counters.
 * Mirrors ChatWorkspace quota effects (tokens/requests/images, fail-closed).
 * Shared cross-instance dedup: ChatWorkspace monta 3-4 instancias a la vez;
 * un solo fetch cada 30s alimenta a todas (ahorra ~70% hits a /api/usage).
 * Cross-tab (P3 2026-09-12, nota sin cambio de conducta): el dedup es por
 * tab (módulo JS aislado por pestaña), no se usa BroadcastChannel a
 * propósito — cada tab salta el tick con document.hidden +
 * visibilitychange, el intervalo es de 180s y el server cachea /api/usage
 * (private max-age=20), así que N tabs visibles cuestan ≤1 hit/30s por tab
 * y las ocultas cuestan 0. Un canal cross-tab añadiría complejidad sin
 * ahorro medible a este ritmo.
 * 2026-09-21 FASE 2 FIXER perf-cpu (research 2026 free-tier N-wakeups: N
 * instancias × 1 intervalo 180s = N wakeups/180s por tab — OK por diseño:
 * cada wakeup reclama el slot compartido (tryClaimSharedUsageFetch, ventana
 * 30s + in-flight) y solo 1 instancia hace red; las demás retornan null sin
 * fetch. Reducir a 1 owner exigiría un singleton con ref-count + handoff en
 * unmount (riesgo de perder el poll si el owner desmonta) — el dedup actual
 * ya da el mismo costo de red (≤1 hit/30s/tab) sin ese riesgo. NO migrar a
 * 1 owner sin medir antes: el ahorro sería solo N-1 timers/180s (despreciable
 * frente a 1 hit de red).
 * SEED=2026-09-25-c6c873bf RUN1 FASE2 PERF-4 (research 2026 free-tier N-wakeups + W3C WCAG22):
 * migrado a 1 owner con ref-count + handoff automático — el riesgo citado
 * arriba queda resuelto: el timer vive en el módulo (no en el owner), cada
 * montaje se suscribe y el último unmount lo suelta; si el primer montaje
 * desmonta, el timer sigue vivo mientras queden suscriptores. 1 wakeup/180s
 * por tab + 1 solo listener visibilitychange. El costo de red sigue ≤1
 * hit/30s/tab (dedup intacto).
 */
let sharedLastFetchMs = 0;
// 2026-09-20 FASE 2 P3-1 (segundo owner): contador de fetches en curso para
// el slot compartido — useDashboardUsage reclama el mismo slot (ver
// tryClaimSharedUsageFetch) en vez de duplicar refs por instancia.
let sharedInflightCount = 0;
let sharedInflight: Promise<{
  plan?: string;
  aiImagesRemaining?: number;
  aiTokensRemaining?: number;
  aiRequestsRemaining?: number;
  aiSmartRequestsRemaining?: number;
  smartTrialRemaining?: number;
  msUntilReset?: number;
  giftNotice?: { dismissed?: boolean } | null;
  exportRemaining?: number;
  exportPerDay?: number;
  /** 2026-10-06 VERTICAL B punto 2: cubo propio fill:daily (mismo valor SSOT). */
  fillRemaining?: number;
  fillPerDay?: number;
} | null> | null = null;

/** Test-only: resetea dedup compartido entre tests. */
export function __resetUsageDedupForTests() {
  sharedLastFetchMs = 0;
  sharedInflightCount = 0;
  sharedInflight = null;
  // SEED=2026-09-25-c6c873bf RUN1 FASE2 PERF-4: el reset también suelta el
  // timer único compartido (si no, un intervalo vivo cruzaría entre tests).
  sharedQuotaSubscribers.clear();
  if (sharedQuotaTimer) {
    try {
      window.clearInterval(sharedQuotaTimer);
    } catch {}
    sharedQuotaTimer = 0;
  }
  if (sharedQuotaVisAttached && typeof document !== "undefined") {
    try {
      document.removeEventListener("visibilitychange", onSharedQuotaVis);
    } catch {}
    sharedQuotaVisAttached = false;
  }
}

/**
 * Reclama el slot de fetch a /api/usage (ventana 30s + in-flight, por tab).
 * Devuelve false cuando otro owner ya pidió hace <30s o hay un fetch en
 * curso — el tick salta sin red (AGENTS.md § Free-tier: ONE owner per poll,
 * module-level dedup ≥30s con shared in-flight). `force` (acción manual del
 * usuario) bypassea la ventana pero igual cuenta el in-flight.
 */
export function tryClaimSharedUsageFetch(force = false): boolean {
  const now = Date.now();
  if (!force && (sharedInflightCount > 0 || now - sharedLastFetchMs < 30_000))
    return false;
  // 2026-10-04 race free smart trial: la ventana SOLO avanza en éxito
  // (fetchUsageShared la marca tras res.ok + JSON). Un fallo/429/null no
  // consume turno — permite reintento inmediato en el siguiente tick.
  sharedInflightCount += 1;
  return true;
}

/** Libera el slot reclamado (llamar en finally del fetch). */
export function releaseSharedUsageFetch(): void {
  sharedInflightCount = Math.max(0, sharedInflightCount - 1);
}

// 2026-10-06 TAREA 6 dashboard-demora (root cause chat→dashboard <30s: el
// dashboard esperaba hasta 35s la ventana 30s aunque NO hubiera fetch en
// curso — la ventana es recencia, no trabajo en vuelo; el HTTP cache
// private max-age=20/SWR-40 ya absorbe el 2º hit). El dashboard distingue:
// in-flight real → espera el slot; solo-ventana → reclama con force y
// fetchea con caché (0-1 hits, instantáneo si el browser cachea).
export function isSharedUsageFetchInflight(): boolean {
  return sharedInflight !== null || sharedInflightCount > 0;
}

// SEED=2026-09-25-c6c873bf RUN1 FASE2 PERF-4 (research 2026 free-tier N-wakeups + W3C WCAG22):
// un solo owner de intervalo por tab — el primer montaje crea el timer, el
// resto se suscribe; el último unmount lo suelta. Antes: N instancias ×
// N intervalos 180s (N wakeups/180s por tab aunque el dedup de red colapsara
// a 1 hit). Ahora: 1 wakeup/180s por tab + 1 solo listener visibilitychange.
// El dedup de red (ventana 30s + in-flight) queda intacto.
let sharedQuotaTimer = 0;
const sharedQuotaSubscribers = new Set<() => void>();
let sharedQuotaVisAttached = false;

function sharedQuotaTick(): void {
  for (const fn of Array.from(sharedQuotaSubscribers)) void fn();
}

function ensureSharedQuotaTimer(): void {
  if (sharedQuotaTimer) return;
  if (typeof document !== "undefined" && document.hidden) return;
  sharedQuotaTimer = window.setInterval(sharedQuotaTick, 180_000);
}

function stopSharedQuotaTimer(): void {
  if (sharedQuotaTimer) {
    window.clearInterval(sharedQuotaTimer);
    sharedQuotaTimer = 0;
  }
}

function onSharedQuotaVis(): void {
  if (typeof document === "undefined") return;
  if (document.hidden) {
    stopSharedQuotaTimer();
    return;
  }
  ensureSharedQuotaTimer();
  sharedQuotaTick();
}

function attachSharedQuotaVis(): void {
  if (sharedQuotaVisAttached || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", onSharedQuotaVis);
  sharedQuotaVisAttached = true;
}

function detachSharedQuotaVis(): void {
  if (!sharedQuotaVisAttached || typeof document === "undefined") return;
  document.removeEventListener("visibilitychange", onSharedQuotaVis);
  sharedQuotaVisAttached = false;
}

/** Castigo post-429: retrasa la ventana compartida extraMs (por tab, no por instancia). */
export function delaySharedUsageFetch(extraMs: number): void {
  sharedLastFetchMs = Date.now() + Math.max(0, extraMs);
}

/**
 * 2026-10-04 RUN13 (race-free-smart-trial dejaba la ventana solo en éxito del
 * chat): el fetch EXITOSO del dashboard también avanza la ventana 30s — si no,
 * los polls del dashboard nunca entran al dedup compartido y cada
 * re-render con refreshUser nuevo (o cada trigger usage-stale/visibility)
 * reclama con éxito y re-fetchea (tormenta N hits/30s por tab). El payload de
 * /api/usage es el mismo para ambos owners: marcar aquí es el dedup diseñado
 * (ONE owner per poll, ventana 30s + in-flight por tab).
 */
export function markSharedUsageFetchSuccess(): void {
  sharedLastFetchMs = Date.now();
}

// 2026-09-19 perf-cpu (research 2026-09-18 SWR dedup + free-tier CPU:
// N montajes × N fetches al mismo endpoint = fan-out; 1 inflight + ventana
// 30s colapsa ChatWorkspace 3-4 instancias + GiftProNotice + PlanChangeNotice
// en 1 hit/30s por tab. Owner compartido — importar desde aquí, no duplicar
// fetches a /api/usage).
// SEED=2026-09-25-c6c873bf RUN1 FASE4 T4 offline/CPU (research 2026 MDN
// AbortSignal.timeout() 2026-09-01 vía webfetch 2026-09-25
// https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal/timeout_static:
// fetch de sonda sin timeout cuelga el in-flight compartido y bloquea la
// ventana 30s de dedup (N ticks desperdiciados + CPU en espera). Timeout 10s
// fail-open (catch → null, el server manda): mismo costo de red en sano,
// cero cuelgues en degradado. Fallback a fetch sin señal si la API no existe
// (navegadores viejos) — nunca romper el poll por el timeout mismo.
function usageFetchSignal(): AbortSignal | undefined {
  try {
    if (typeof AbortSignal !== "undefined" && "timeout" in AbortSignal) {
      return AbortSignal.timeout(10_000);
    }
  } catch {}
  return undefined;
}

export async function fetchUsageShared(force = false) {
  if (sharedInflight) return sharedInflight;
  // 2026-09-20 FASE 2 P3-1: el dedup vive en tryClaimSharedUsageFetch
  // (slot compartido con useDashboardUsage, segundo owner de /api/usage).
  if (!tryClaimSharedUsageFetch(force)) return null;
  sharedInflight = (async () => {
    try {
      // Sin cache:no-store: respeta private max-age=20 del server.
      const res = await fetch("/api/usage", {
        headers: { Accept: "application/json" },
        signal: usageFetchSignal(),
      });
      if (!res.ok) return null;
      const data = (await res.json().catch(() => null)) as {
        plan?: string;
        aiImagesRemaining?: number;
        aiTokensRemaining?: number;
        aiRequestsRemaining?: number;
        aiSmartRequestsRemaining?: number;
        smartTrialRemaining?: number;
        msUntilReset?: number;
        giftNotice?: { dismissed?: boolean } | null;
        exportRemaining?: number;
        exportPerDay?: number;
        /** 2026-10-06 VERTICAL B punto 2: cubo propio fill:daily. */
        fillRemaining?: number;
        fillPerDay?: number;
      } | null;
      if (data) sharedLastFetchMs = Date.now();
      return data;
    } catch {
      return null;
    } finally {
      sharedInflight = null;
      releaseSharedUsageFetch();
    }
  })();
  return sharedInflight;
}
export function useChatQuotas(userId: string | null): UseChatQuotasReturn {
  const [imageQuotaRemaining, setImageQuotaRemaining] = useState<number | null>(
    null,
  );
  const [imageQuotaResetIn, setImageQuotaResetIn] = useState<number | null>(
    null,
  );
  const [chatQuotaRemaining, setChatQuotaRemaining] = useState<number | null>(
    null,
  );
  const [smartQuotaRemaining, setSmartQuotaRemaining] = useState<number | null>(
    null,
  );
  const [smartTrialRemaining, setSmartTrialRemaining] = useState<number | null>(
    null,
  );
  const [tokensRemaining, setTokensRemaining] = useState<number | null>(null);
  const [plan, setPlan] = useState<PlanTier | null>(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [quotaResetInSeconds, setQuotaResetInSeconds] = useState<number | null>(
    null,
  );

  const refresh = async (force = false) => {
    if (!userId) return;
    try {
      const data = await fetchUsageShared(force);
      if (!data) return;
      // 2026-09-23 TAREA D: el plan alimenta el upsell del banner quota_hit
      // (Free→Go, Go→Pro… vía getUpsellSnapshot; SSOT PLAN_CONFIG).
      if (
        data.plan === "free" ||
        data.plan === "go" ||
        data.plan === "pro" ||
        data.plan === "max" ||
        data.plan === "maxplus"
      )
        setPlan(data.plan);
      if (typeof data.aiImagesRemaining === "number")
        setImageQuotaRemaining(data.aiImagesRemaining);
      if (typeof data.aiSmartRequestsRemaining === "number")
        setSmartQuotaRemaining(data.aiSmartRequestsRemaining);
      // 2026-09-26 Smart trial lifetime: solo free expone el campo (pagos lo
      // omiten → null, sin contador ni nudge).
      if (typeof data.smartTrialRemaining === "number")
        setSmartTrialRemaining(data.smartTrialRemaining);
      if (typeof data.aiTokensRemaining === "number") {
        setTokensRemaining(data.aiTokensRemaining);
      }
      if (
        typeof data.aiTokensRemaining === "number" ||
        typeof data.aiRequestsRemaining === "number"
      ) {
        const tokensLeft =
          typeof data.aiTokensRemaining === "number"
            ? data.aiTokensRemaining
            : Infinity;
        const reqLeft =
          typeof data.aiRequestsRemaining === "number"
            ? data.aiRequestsRemaining
            : Infinity;
        setChatQuotaRemaining(Math.min(tokensLeft, reqLeft));
      }
      if (typeof data.msUntilReset === "number") {
        const secs = Math.ceil(data.msUntilReset / 1000);
        setImageQuotaResetIn(secs);
        if (
          secs &&
          (typeof data.aiTokensRemaining === "number" ||
            typeof data.aiRequestsRemaining === "number")
        ) {
          // only seed if unknown — prevents overwriting a more precise error retry
          setQuotaResetInSeconds((prev) => (prev === null ? secs : prev));
        }
      }
    } catch {
      // fail open — server will enforce strictly
    }
  };

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- reset de logout: estado externo de auth → estado de cuota, sync intencional */
    if (!userId) {
      setImageQuotaRemaining(null);
      setImageQuotaResetIn(null);
      setChatQuotaRemaining(null);
      setSmartQuotaRemaining(null);
      setSmartTrialRemaining(null);
      setTokensRemaining(null);
      setPlan(null);
      return;
    }
    /* eslint-enable react-hooks/set-state-in-effect */
    let cancelled = false;
    const wrappedRefresh = async () => {
      if (cancelled) return;
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      if (typeof document !== "undefined" && document.hidden) return;
      await refresh();
    };
    void wrappedRefresh();
    // 60s -> 180s: la cuota resetea a medianoche UTC, nada visible cambia.
    // El dedup compartido de 30s absorbe las 3-4 instancias montadas.
    // 2026-09-21 cero-wakeups en oculta (paridad StatusDashboard): start/stop
    // por visibilitychange — en background no queda timer vivo.
    // SEED=2026-09-25-c6c873bf RUN1 FASE2 PERF-4: suscripción al timer único
    // por tab (ver ensureSharedQuotaTimer) — 1 intervalo aunque monten N
    // instancias; el handoff es automático (el último unmount lo suelta).
    sharedQuotaSubscribers.add(wrappedRefresh);
    attachSharedQuotaVis();
    ensureSharedQuotaTimer();
    return () => {
      cancelled = true;
      sharedQuotaSubscribers.delete(wrappedRefresh);
      if (sharedQuotaSubscribers.size === 0) {
        stopSharedQuotaTimer();
        detachSharedQuotaVis();
      }
    };
    // quotaResetInSeconds is intentionally not a dep here — we read it via functional update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // 2026-08-29 Task38: quotaExceeded derivado (no state) + poll offline/hidden guard
  const derivedExceeded = useMemo(
    () => chatQuotaRemaining !== null && chatQuotaRemaining <= 0,
    [chatQuotaRemaining],
  );
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- derived→state: chatQuotaRemaining<=0 ⇒ quotaExceeded + seed del reset, sync intencional */
    if (derivedExceeded !== quotaExceeded) setQuotaExceeded(derivedExceeded);
    if (derivedExceeded && imageQuotaResetIn)
      setQuotaResetInSeconds((prev) => prev ?? imageQuotaResetIn);
    if (!derivedExceeded && quotaExceeded) setQuotaResetInSeconds(null);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [derivedExceeded, quotaExceeded, imageQuotaResetIn]);

  return {
    imageQuotaRemaining,
    setImageQuotaRemaining,
    imageQuotaResetIn,
    setImageQuotaResetIn,
    chatQuotaRemaining,
    setChatQuotaRemaining,
    plan,
    setPlan,
    smartQuotaRemaining,
    setSmartQuotaRemaining,
    smartTrialRemaining,
    setSmartTrialRemaining,
    tokensRemaining,
    setTokensRemaining,
    quotaExceeded,
    setQuotaExceeded,
    quotaResetInSeconds,
    setQuotaResetInSeconds,
    refresh,
  };
}
